/* eslint-disable react-hooks/exhaustive-deps */
import React from 'react';
import { Button, Spinner, Card, Container, Row, Col } from 'react-bootstrap';
import TextIconInput from 'components/input/TextIconInput';
import LoadingContent from 'components/LoadingContent';
import SelectInput from 'components/input/SelectInput';
import {
  Avatar,
  Box,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from '@mui/material';
import { FileToBase64, finishYearOption } from 'components/helper/helper';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import {
  getContactDetail,
  getCurrentBasicDetail,
  update,
  updateContact,
} from 'app/basicInformationSlice/basicInformationSlice';
import basicInformationServices from 'services/basicInformationApi';
import toast from 'react-hot-toast';
import { useHistory } from 'react-router-dom';
import { routers } from 'router/router';
import { logout } from 'app/basicInformationSlice/basicInformationSlice';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { identifyUserpilot } from 'utils/userpilotClient';
import TextInput from 'components/input/textInput';
import { USER_ROLE } from 'constants/common';
import {
  CloudUploadOutlined,
  DeleteOutlined,
  PersonOutlined,
  EmailOutlined,
  // SchoolOutlined,
  // CalendarTodayOutlined,
} from '@mui/icons-material';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import VerifyCodeInput from 'components/input/VerifyCodeInput';
import SelectPhoneCodeInput from 'components/input/SelectPhoneCodeInput';
import { sendVerificationCode, checkVerificationCode } from 'app/authorization/authorizationSlice';
import SettingsNotification from './SettingsNotification';
import { getAccessToken, getUserId, removeAccessToken, removeUserId } from 'utils/sessionStorage';

const SettingsPersonalInformation = () => {
  const fileInputRef: any = React.useRef();
  const [loading, setLoading] = React.useState(false);
  const [waiting, setWaiting] = React.useState(false);
  const [changeEmailModalOpen, setChangeEmailModalOpen] = React.useState(false);
  const [changingEmail, setChangingEmail] = React.useState(false);
  const dispatch = useAppDispatch();
  const history = useHistory();
  let studentId = getUserId();
  let token = getAccessToken();

  const handleChangeImage = async (fileList: any) => {
    if (!fileList?.[0]) return;
    if (fileList[0].size > 10000000) {
      toast.error('Only accept less than 10mb image');
      return;
    }
    setLoading(true);
    try {
      await basicInformationServices.updateProfilePicture(fileList[0], studentId);
      const res: any = await dispatch(getCurrentBasicDetail(studentId));
      if (res.payload) {
        formik.setValues(res.payload);
        window.location.reload();
      }
    } catch (e) {
      console.error(e);
      toast.error('Failed to update profile picture');
    } finally {
      setLoading(false);
      formik.setStatus('idle');
    }
  };
  const information = useAppSelector((store) => store.basicInformation.myBasicInformation);
  const role = information?.user_role ?? '';

  const formik = useFormik({
    initialValues: {
      first_name: '',
      last_name: '',
      email: '',
      secondary_email: '',
      school_or_organization_work_with: '',
      year_finish_high_school: undefined,
      phone_number: '',
      country_code: '+1',
    },
    validationSchema: Yup.object({
      email: Yup.string().email('Invalid email').required('Required'),
      first_name: Yup.string().required('Required'),
      last_name: Yup.string().required('Required'),
      secondary_email: Yup.string().email('Invalid email').nullable(),
    }),
    onSubmit: async (values) => {
      setWaiting(true);
      identifyUserpilot(studentId, role, {
        name: information?.first_name,
        created_at: information?.created_at,
        firstName: values.first_name,
        lastName: values.last_name,
        email: values.email,
        school: values.school_or_organization_work_with,
        userType: sessionStorage.getItem('userRole'),
        graduationYear: values.year_finish_high_school,
      });

      window.Intercom('update', {
        graduation_year: values?.year_finish_high_school,
      });

      await dispatch(update(values));

      setWaiting(false);

      // reload data to update topbar/header
      setTimeout(function () {
        window.location.reload();
      }, 1000);
    },
  });

  const [profilePicture, setProfilePicture] = React.useState<any>();
  const personalStore = useAppSelector((store) => store.basicInformation.myBasicInformation);
  const [contactDetail, setContactDetail] = React.useState<any>();
  const handleLogout = async () => {
    // Call API logout first
    try {
      if (studentId && token) {
        await basicInformationServices.userLogout(studentId, token);
      }
    } catch (error) {
      console.error('Error calling logout API:', error);
      // Continue with logout even if API call fails
    }

    // Then perform local logout
    dispatch(logout());
    removeAccessToken();
    removeUserId();
    history.push(routers.login.path);
  };

  // Change Email Form
  const changeEmailFormik = useFormik({
    initialValues: {
      new_email: '',
    },
    validationSchema: Yup.object({
      new_email: Yup.string().email('Invalid email').required('Required'),
    }),
    onSubmit: async (values) => {
      if (!studentId) {
        toast.error('User ID not found');
        return;
      }

      setChangingEmail(true);
      try {
        const res = await basicInformationServices.changeEmail(Number(studentId), values.new_email);
        if (res) {
          toast.success('Email changed successfully. You will be logged out.');
          setTimeout(async () => {
            await handleLogout();
          }, 1500);
        }
      } catch (error: any) {
        console.error('Error changing email:', error);
        toast.error(error?.response?.data?.message || 'Failed to change email');
      } finally {
        setChangingEmail(false);
      }
    },
  });

  React.useEffect(() => {
    studentId = getUserId();
    token = getAccessToken();
    if (token && studentId) {
      const profilePictureName = personalStore?.profile_picture_file_name ?? null;
      if (profilePictureName) {
        setIsFetchingAvatar(true);
        basicInformationServices
          .getProfilePicture(profilePictureName)
          .then(async (res) => {
            const blob = new Blob([res], { type: 'image/png;charset=utf-8' });
            const url = await FileToBase64(blob);
            setProfilePicture(url);

            const data = { id: studentId, url_pic: url };
            localStorage.setItem('cachedProfilePic', JSON.stringify(data));
          })
          .catch(() => {})
          .finally(() => setIsFetchingAvatar(false));
      }
    } else {
      handleLogout();
    }
  }, [personalStore?.profile_picture_file_name, personalStore?.user_role, studentId]);

  const [initialPhoneNumber, setInitialPhoneNumber] = React.useState<string>('');
  const [verifiedPhoneNumber, setVerifiedPhoneNumber] = React.useState<string>('');
  const [isInitialLoad, setIsInitialLoad] = React.useState(true);
  const [isContactDetailLoaded, setIsContactDetailLoaded] = React.useState(false);
  const [isBasicInfoLoaded, setIsBasicInfoLoaded] = React.useState(false);

  // OTP Verification Modal States
  const [openVerifyModal, setOpenVerifyModal] = React.useState(false);
  const [verifyCode, setVerifyCode] = React.useState<string | undefined>(undefined);
  const [timer, setTimer] = React.useState(60);
  const [isSendingCode, setIsSendingCode] = React.useState(false);
  const [isVerifyingPhone, setIsVerifyingPhone] = React.useState(false);
  const [isResendingCode, setIsResendingCode] = React.useState(false);
  const [isRemovingPicture, setIsRemovingPicture] = React.useState(false);
  /** Chỉ tải ảnh đại diện — không dùng chung `loading` để tránh che cả trang */
  const [isFetchingAvatar, setIsFetchingAvatar] = React.useState(false);
  const timerIntervalRef = React.useRef<any>();

  // API error when update contact returns 400 (e.g. phone already registered)
  const [phoneNumberApiError, setPhoneNumberApiError] = React.useState<string | null>(null);
  /** True while debouncing + updateContact (existence / save check) — hide Verify until done */
  const [isPhoneContactSaving, setIsPhoneContactSaving] = React.useState(false);

  // Lỗi trùng SĐT: sau ~2s ẩn thông báo và khôi phục số đã lưu (user vẫn có thể sửa sớm hơn — effect sẽ clear timer)
  React.useEffect(() => {
    if (!phoneNumberApiError) return;
    const revertTo = initialPhoneNumber;
    const t = window.setTimeout(() => {
      setPhoneNumberApiError(null);
      formik.setFieldValue('phone_number', revertTo);
    }, 2000);
    return () => clearTimeout(t);
  }, [phoneNumberApiError]);

  // Load contact detail
  React.useEffect(() => {
    studentId = getUserId();
    token = getAccessToken();
    if (token && studentId) {
      dispatch(getContactDetail(studentId))
        .then((res: any) => {
          if (res.payload) {
            setContactDetail(res.payload);
            const phoneNumber = res.payload?.phone_number || '';
            const countryCode = res.payload?.country_code || '+1';
            setInitialPhoneNumber(phoneNumber);
            formik.setFieldValue('phone_number', phoneNumber);
            formik.setFieldValue('country_code', countryCode);

            // Lưu số điện thoại đã verify (nếu có)
            if (res.payload?.is_phone_verify && phoneNumber) {
              setVerifiedPhoneNumber(phoneNumber);
            } else {
              setVerifiedPhoneNumber('');
            }
          } else {
            setContactDetail(null);
            setInitialPhoneNumber('');
          }
          setIsContactDetailLoaded(true);
        })
        .catch(() => {
          setContactDetail(null);
          setInitialPhoneNumber('');
          setIsContactDetailLoaded(true);
        });
    } else {
      handleLogout();
    }
  }, []);

  // Load basic information
  React.useEffect(() => {
    studentId = getUserId();
    token = getAccessToken();
    if (token && studentId) {
      if (!personalStore?.id) {
        setLoading(true);
        dispatch(getCurrentBasicDetail(studentId)).then((res: any) => {
          if (res.payload) {
            formik.setValues(res.payload);
          }
          setLoading(false);
          setIsInitialLoad(false);
          setIsBasicInfoLoaded(true);
        });
      } else {
        formik.setValues(personalStore);
        setIsInitialLoad(false);
        setIsBasicInfoLoaded(true);
      }
    }
  }, []);

  // Re-apply phone_number and country_code from contactDetail after both are loaded
  React.useEffect(() => {
    if (isBasicInfoLoaded && isContactDetailLoaded && contactDetail) {
      if (contactDetail.phone_number) {
        formik.setFieldValue('phone_number', contactDetail.phone_number);
      }
      if (contactDetail.country_code) {
        formik.setFieldValue('country_code', contactDetail.country_code);
      }
    }
  }, [isBasicInfoLoaded, isContactDetailLoaded]);

  // Watch for phone_number and country_code changes and update contact
  React.useEffect(() => {
    // Skip if initial load or phone_number hasn't changed
    if (isInitialLoad || formik.values.phone_number === initialPhoneNumber) {
      setIsPhoneContactSaving(false);
      return;
    }

    // Clear previous API error when user changes input
    setPhoneNumberApiError(null);
    setIsPhoneContactSaving(true);

    const updateContactDebounce = setTimeout(async () => {
      studentId = getUserId();
      if (studentId && contactDetail) {
        try {
          // Nếu số điện thoại thay đổi so với số đã verify, reset trạng thái verify
          const phoneChanged = formik.values.phone_number !== verifiedPhoneNumber;
          const updatedData = {
            ...contactDetail,
            student_id: studentId,
            phone_number: formik.values.phone_number,
            country_code: formik.values.country_code || '+1',
            // Reset verify status nếu số điện thoại thay đổi
            ...(phoneChanged && { is_phone_verify: false }),
          };

          await dispatch(updateContact(updatedData)).unwrap();

          setPhoneNumberApiError(null);
          // Update the initial value after successful update
          setInitialPhoneNumber(formik.values.phone_number);
          // Update contactDetail state
          setContactDetail(updatedData);

          // Reset verified phone number nếu số đã thay đổi
          if (phoneChanged) {
            setVerifiedPhoneNumber('');
          }
        } catch (error: any) {
          // .unwrap() rejects with the rejectWithValue payload: { statusCode, message }
          const payload = error?.payload ?? error?.response?.data ?? error;
          const statusCode = payload?.statusCode;
          const message = payload?.message;
          if (statusCode === 400 && message) {
            setPhoneNumberApiError(message);
          } else {
            console.error('Error updating contact:', error);
          }
        } finally {
          setIsPhoneContactSaving(false);
        }
      } else {
        setIsPhoneContactSaving(false);
      }
    }, 1000); // Debounce for 1 second to avoid excessive API calls

    return () => clearTimeout(updateContactDebounce);
  }, [formik.values.phone_number, formik.values.country_code]);

  const verifyPhoneNumber = async () => {
    if (!formik.values.phone_number) {
      toast.error('Please enter a phone number first');
      return;
    }

    setIsSendingCode(true);
    setOpenVerifyModal(true);

    try {
      const res = await dispatch(
        sendVerificationCode({
          student_id: studentId,
          country_code: formik.values.country_code || '+1',
          phone_number: formik.values.phone_number,
        })
      );

      if (res.payload) {
        setTimer(60);
      } else {
        setOpenVerifyModal(false);
      }
    } catch (error) {
      console.error('Failed to send verification code:', error);
      toast.error('Failed to send verification code. Please try again.');
      setOpenVerifyModal(false);
    } finally {
      setIsSendingCode(false);
    }
  };

  const handleVerifyCode = async () => {
    if (!verifyCode || verifyCode.length !== 4) {
      toast.error('Please enter a valid 4-digit code');
      return;
    }

    setIsVerifyingPhone(true);
    try {
      const res = await dispatch(
        checkVerificationCode({
          verification_code: verifyCode,
          student_id: studentId,
          country_code: formik.values.country_code || '+1',
          phone_number: formik.values.phone_number,
        })
      );

      if (res.payload && res.payload.statusCode === 200) {
        toast.success('Phone number verified successfully!');

        // Update contact detail to mark as verified
        const updatedContact = {
          ...contactDetail,
          student_id: studentId,
          phone_number: formik.values.phone_number,
          country_code: formik.values.country_code || '+1',
          is_phone_verify: true,
        };

        // await dispatch(updateContact(updatedContact));
        setContactDetail(updatedContact);

        // Lưu số điện thoại đã verify
        setVerifiedPhoneNumber(formik.values.phone_number);

        setOpenVerifyModal(false);
        setVerifyCode(undefined);
        setTimer(60);
      } else {
        toast.error('Invalid verification code. Please try again.');
      }
    } catch (error) {
      console.error('Failed to verify code:', error);
      toast.error('Failed to verify code. Please try again.');
    } finally {
      setIsVerifyingPhone(false);
    }
  };

  const handleResendCode = async () => {
    setIsResendingCode(true);
    setTimer(60);
    try {
      const res = await dispatch(
        sendVerificationCode({
          student_id: studentId,
          country_code: formik.values.country_code || '+1',
          phone_number: formik.values.phone_number,
        })
      );

      if (res.payload) {
        toast.success('Verification code sent successfully!');
      }
    } catch (error) {
      console.error('Failed to resend code:', error);
      toast.error('Failed to resend code. Please try again.');
    } finally {
      setIsResendingCode(false);
    }
  };

  // Timer effect for OTP modal
  React.useEffect(() => {
    if (openVerifyModal && !isSendingCode && timer > 0) {
      timerIntervalRef.current = setInterval(() => {
        setTimer((prev) => {
          if (prev <= 1) {
            clearInterval(timerIntervalRef.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
    };
  }, [openVerifyModal, isSendingCode, timer]);

  const removePictureHandle = async () => {
    studentId = getUserId();
    token = getAccessToken();
    if (token && studentId) {
      setIsRemovingPicture(true);
      setProfilePicture(null);
      try {
        const profilePictureName = personalStore?.profile_picture_file_name ?? null;
        await basicInformationServices.removeProfilePicture(studentId, profilePictureName);
        window.location.reload();
      } catch (e) {
        console.error(e);
        toast.error('Failed to remove profile picture');
        setIsRemovingPicture(false);
      }
    } else {
      handleLogout();
    }
  };

  /** Chặn tương tác form cho đến khi basic info + contact (SĐT) load xong — tránh race / lỗi */
  const isInitialDataLoading = !isBasicInfoLoaded || !isContactDetailLoaded;
  const isBlockingUi = loading || isInitialDataLoading || isRemovingPicture;

  return isBlockingUi ? (
    <LoadingContent />
  ) : (
    <Container fluid className="modern-settings-container">
      <Box className="modern-settings-wrapper">
        {/* Header Section */}
        {/* Profile Picture Section */}
        <Card className="modern-card profile-picture-card">
          <Card.Body className="modern-card-body">
            <Box className="profile-picture-section">
              <Box className="profile-picture-header">
                <Box className="section-icon-wrapper">
                  <PersonOutlined className="section-icon" />
                </Box>
                <Typography variant="h6" className="section-title">
                  Profile Picture
                </Typography>
              </Box>

              <Box className="profile-picture-content">
                <Box className="avatar-container" sx={{ position: 'relative' }}>
                  <Avatar
                    className="modern-avatar"
                    alt="Profile Picture"
                    src={profilePicture}
                    sx={{
                      width: { xs: 120, sm: 140, md: 160 },
                      height: { xs: 120, sm: 140, md: 160 },
                      border: '4px solid #f0f4ff',
                      boxShadow: '0 8px 32px rgba(77, 136, 240, 0.15)',
                      opacity: isFetchingAvatar ? 0.5 : 1,
                    }}
                  />
                  {isFetchingAvatar && (
                    <CircularProgress
                      size={40}
                      sx={{
                        position: 'absolute',
                        left: '50%',
                        top: '50%',
                        marginLeft: '-20px',
                        marginTop: '-20px',
                      }}
                    />
                  )}
                  <Box className="avatar-overlay">
                    <IconButton
                      className="upload-button"
                      onClick={() => fileInputRef.current.click()}
                      disabled={loading || isPhoneContactSaving || isFetchingAvatar || isSendingCode || isVerifyingPhone}
                      sx={{
                        backgroundColor: 'rgba(77, 136, 240, 0.9)',
                        color: 'white',
                        '&:hover': {
                          backgroundColor: 'rgba(77, 136, 240, 1)',
                          transform: 'scale(1.05)',
                        },
                      }}
                    >
                      <CloudUploadOutlined />
                    </IconButton>
                  </Box>
                  <input
                    type="file"
                    accept="image/*"
                    className="d-none"
                    onChange={(e) => handleChangeImage(e.target.files)}
                    ref={fileInputRef}
                  />
                </Box>

                <Box className="profile-actions">
                  <Button
                    variant="outlined"
                    className="modern-button upload-button-text"
                    onClick={() => fileInputRef.current.click()}
                      disabled={loading || isPhoneContactSaving || isFetchingAvatar || isSendingCode || isVerifyingPhone}
                    >
                      <CloudUploadOutlined className="me-2" />
                      Upload New Photo
                    </Button>
                  <Button
                    variant="outlined"
                    className="modern-button remove-button"
                    onClick={() => removePictureHandle()}
                    disabled={loading || isPhoneContactSaving || isRemovingPicture || isFetchingAvatar || isSendingCode || isVerifyingPhone}
                  >
                    <DeleteOutlined className="me-2" />
                    Remove Photo
                  </Button>
                </Box>
              </Box>
            </Box>
          </Card.Body>
        </Card>

        {/* Basic Details Section */}
        <Card className="modern-card basic-details-card">
          <Card.Body className="modern-card-body">
            <Box className="basic-details-section">
              <Box className="section-header">
                <Box className="section-icon-wrapper">
                  <PersonOutlined className="section-icon" />
                </Box>
                <Typography variant="h6" className="section-title">
                  Basic Details
                </Typography>
              </Box>

              <Box className="form-grid">
                {/* Name Fields */}
                <Box className="form-row">
                  <Box className="form-field">
                    <TextIconInput
                      className="modern-input"
                      icon={<PersonOutlined />}
                      iconClass="modern-input-icon"
                      mdCol={'12'}
                      label={'First Name'}
                      name={'first_name'}
                      values={formik.values.first_name}
                      onChange={formik.handleChange}
                      feedback={
                        formik.touched.first_name &&
                        formik.errors.first_name && (
                          <span className="error-message">{formik.errors.first_name}</span>
                        )
                      }
                    />
                  </Box>
                  <Box className="form-field">
                    <TextIconInput
                      className="modern-input"
                      icon={<PersonOutlined />}
                      iconClass="modern-input-icon"
                      mdCol={'12'}
                      label={'Last Name'}
                      name={'last_name'}
                      values={formik.values.last_name}
                      onChange={formik.handleChange}
                      feedback={
                        formik.touched.last_name &&
                        formik.errors.last_name && (
                          <span className="error-message">{formik.errors.last_name}</span>
                        )
                      }
                    />
                  </Box>
                </Box>

                {/* School/Year Field */}
                <Box className="form-row">
                  <Box className="form-field full-width">
                    {sessionStorage.getItem('userRole') === USER_ROLE.counselor ? (
                      <TextInput
                        label={'What school or organization do you work with?'}
                        name={'school_or_organization_work_with'}
                        values={formik.values.school_or_organization_work_with}
                        onChange={formik.handleChange}
                      />
                    ) : (
                      <SelectInput
                        form={formik}
                        values={formik.values.year_finish_high_school}
                        onChange={formik.handleChange}
                        name={'year_finish_high_school'}
                        label={'When will you finish high school?'}
                        option={finishYearOption}
                      />
                    )}
                  </Box>
                </Box>

                {/* Email Fields */}
                <Box className="form-row">
                  <Box className="form-field">
                    <TextIconInput
                      className="modern-input disabled-input"
                      icon={<EmailOutlined />}
                      iconClass="modern-input-icon"
                      mdCol={'12'}
                      label={'Email'}
                      name={'email'}
                      values={formik.values.email}
                      onChange={formik.handleChange}
                      disabled
                      feedback={
                        formik.touched.email &&
                        formik.errors.email && (
                          <span className="error-message">{formik.errors.email}</span>
                        )
                      }
                    />
                    <Box sx={{ mt: 1 }}>
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => setChangeEmailModalOpen(true)}
                        style={{ textTransform: 'none' }}
                      >
                        Change Email
                      </Button>
                    </Box>
                  </Box>
                  {/* <Box className="form-field">
                    <TextIconInput
                      className="modern-input"
                      icon={<EmailOutlined />}
                      iconClass="modern-input-icon"
                      mdCol={'12'}
                      label={'Secondary Email'}
                      name={'secondary_email'}
                      values={formik.values.secondary_email}
                      onChange={formik.handleChange}
                      feedback={
                        formik.touched.secondary_email &&
                        formik.errors.secondary_email && (
                          <span className="error-message">{formik.errors.secondary_email}</span>
                        )
                      }
                    />
                  </Box> */}
                  <Box className="form-field">
                    <Row>
                      <Col md={4}>
                        <Box sx={{ pr: 2 }}>
                          <SelectPhoneCodeInput
                            values={formik.values.country_code}
                            onChange={formik.handleChange}
                            name={'country_code'}
                            label={'Country Code'}
                          />
                        </Box>
                      </Col>
                      <Col md={8}>
                        <TextIconInput
                          className="modern-input"
                          icon={<PhoneAndroidIcon />}
                          iconClass="modern-input-icon"
                          mdCol={'12'}
                          label={'Phone Number'}
                          name={'phone_number'}
                          values={formik.values.phone_number}
                          onChange={formik.handleChange}
                          feedback={
                            (formik.touched.phone_number &&
                              formik.errors.phone_number && (
                                <span className="error-message">{formik.errors.phone_number}</span>
                              )) ||
                            (phoneNumberApiError && (
                              <span className="error-message">{phoneNumberApiError}</span>
                            ))
                          }
                        />
                      </Col>
                    </Row>
                    {(() => {
                      const currentPhone = formik.values.phone_number;
                      const hasPhone = !!currentPhone;
                      const isVerified = currentPhone === verifiedPhoneNumber && contactDetail?.is_phone_verify;
                      const canShowVerifyActions = hasPhone && !isVerified && !phoneNumberApiError;
                      const shouldShowVerify = canShowVerifyActions && !isPhoneContactSaving;

                      return (
                        <>
                          {canShowVerifyActions && isPhoneContactSaving && (
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1.5,
                                mt: 1,
                                color: 'text.secondary',
                              }}
                            >
                              <CircularProgress size={18} thickness={5} />
                              <Typography variant="caption">
                                Checking phone number…
                              </Typography>
                            </Box>
                          )}
                          {shouldShowVerify && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
                              <Typography variant="caption" className="modern-input-subtitle">
                                <span className="error-message">
                                  {verifiedPhoneNumber && currentPhone !== verifiedPhoneNumber
                                    ? 'Phone number changed. Please verify again.'
                                    : 'Phone number is not verified'}
                                </span>
                              </Typography>
                              <Button
                                variant="primary"
                                onClick={() => verifyPhoneNumber()}
                                disabled={isSendingCode}
                                style={{ fontSize: '0.875rem', padding: '4px 12px' }}
                              >
                                {isSendingCode ? (
                                  <>
                                    <Spinner size="sm" animation="border" className="me-2" />
                                    Sending code…
                                  </>
                                ) : (
                                  'Verify Phone Number'
                                )}
                              </Button>
                            </Box>
                          )}
                        </>
                      );
                    })()}
                  </Box>
                </Box>
              </Box>

              {/* <SettingsNotification /> */}

              {/* Action Buttons */}

            </Box>
          </Card.Body>
        </Card>


        {/* Notification Section */}
        <Card className="modern-card notification-card">
          <Card.Body className="modern-card-body">
            <Box className="notification-section">
              <Box className="section-header">
                <SettingsNotification />
              </Box>
            </Box>
          </Card.Body>
        </Card>
      </Box>

      <Box className="form-actions mb-3">
        <Button
          variant="primary"
          className="modern-save-button"
          onClick={() => formik.handleSubmit()}
          disabled={waiting || isPhoneContactSaving || isSendingCode || isVerifyingPhone}
        >
          {waiting ? (
            <>
              <Spinner size="sm" animation="border" variant="white" className="me-2" />
              Updating...
            </>
          ) : (
            'Save Changes'
          )}
        </Button>
      </Box>
      {/* OTP Verification Modal */}
      <Dialog
        open={openVerifyModal}
        onClose={() => {
          if (isSendingCode || isVerifyingPhone) return;
          setOpenVerifyModal(false);
          setVerifyCode(undefined);
          setTimer(60);
          setIsSendingCode(false);
          setIsVerifyingPhone(false);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h5" component="div">
            Verify Phone Number
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ py: 2 }}>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Enter the 4-digit code sent to{' '}
              <strong>{formik.values.country_code || '+1'}{formik.values.phone_number || ''}</strong>
            </Typography>
            {isSendingCode ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
                <Spinner animation="border" variant="primary" />
                <Typography variant="body2" sx={{ ml: 2 }}>
                  Sending verification code...
                </Typography>
              </Box>
            ) : (
              <Box sx={{ position: 'relative', minHeight: isVerifyingPhone ? 200 : 'auto' }}>
                {isVerifyingPhone && (
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      zIndex: 2,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 1.5,
                      borderRadius: 1,
                      backgroundColor: 'rgba(255,255,255,0.85)',
                    }}
                  >
                    <CircularProgress size={40} />
                    <Typography variant="body2" color="text.secondary">
                      Verifying code…
                    </Typography>
                  </Box>
                )}
                <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
                  <VerifyCodeInput onChange={(value) => setVerifyCode(value.join(''))} />
                </Box>
                {timer <= 0 ? (
                  <Button
                    variant="outline-primary"
                    className="w-100 mb-2"
                    onClick={handleResendCode}
                    disabled={isResendingCode || isVerifyingPhone}
                  >
                    {isResendingCode ? (
                      <>
                        <Spinner size="sm" animation="border" className="me-2" />
                        Sending…
                      </>
                    ) : (
                      'Resend Code'
                    )}
                  </Button>
                ) : (
                  <Typography className="text-center mb-2" variant="body2" color="text.secondary">
                    Resend code in {timer}s
                  </Typography>
                )}
                <Button
                  variant="primary"
                  className="w-100"
                  onClick={handleVerifyCode}
                  disabled={!verifyCode || verifyCode.length !== 4 || isVerifyingPhone}
                >
                  {isVerifyingPhone ? (
                    <>
                      <Spinner size="sm" animation="border" variant="white" className="me-2" />
                      Verifying…
                    </>
                  ) : (
                    'Verify Code'
                  )}
                </Button>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpenVerifyModal(false);
              setVerifyCode(undefined);
              setTimer(60);
              setIsSendingCode(false);
              setIsVerifyingPhone(false);
            }}
            disabled={isSendingCode || isVerifyingPhone}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Change Email Modal */}
      <Dialog
        open={changeEmailModalOpen}
        onClose={() => {
          setChangeEmailModalOpen(false);
          changeEmailFormik.resetForm();
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Change Email</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="warning.main" sx={{ fontWeight: 500 }}>
              ⚠️ Warning: Changing your email will log you out of your current session.
              You will need to log in again using your new email address.
            </Typography>
          </Box>
          <form onSubmit={changeEmailFormik.handleSubmit}>
            <Box sx={{ mt: 2 }}>
              <TextIconInput
                className="modern-input"
                icon={<EmailOutlined />}
                iconClass="modern-input-icon"
                mdCol={'12'}
                label={'New Email'}
                name={'new_email'}
                values={changeEmailFormik.values.new_email}
                onChange={changeEmailFormik.handleChange}
                feedback={
                  changeEmailFormik.touched.new_email &&
                  changeEmailFormik.errors.new_email && (
                    <span className="error-message">{changeEmailFormik.errors.new_email}</span>
                  )
                }
              />
            </Box>
          </form>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setChangeEmailModalOpen(false);
              changeEmailFormik.resetForm();
            }}
            disabled={changingEmail}
          >
            Close
          </Button>
          <Button
            variant="primary"
            onClick={() => changeEmailFormik.handleSubmit()}
            disabled={changingEmail || !changeEmailFormik.values.new_email}
          >
            {changingEmail ? 'Updating...' : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default SettingsPersonalInformation;
